//-- copyright
// OpenProject Resource Planner (multi-resource) management plugin.
//++

import {
  ChangeDetectorRef, Component, Input, OnInit, inject,
} from '@angular/core';
import { WorkPackageResource } from 'core-app/features/hal/resources/work-package-resource';
import {
  buildTimephased, TpCalendar, TpPeriod, CalLookup, parseISO, isoDate,
} from '../shared/timephased.util';

interface KV { key:string; name:string; }
interface RateRow { effectiveDate:string|null; rate:number; }
interface Availability { from:string|null; to:string|null; maxUnits:number; }
interface PickRes {
  id:number; name:string; initials:string|null; resourceType:string|null; maxUnits:number;
  rate:number; currency:string; committedElsewhere:number;
  calendarId:number|null; rateTables:Record<string,RateRow[]>; availabilities:Availability[];
}
interface RpCal { id:number; isDefault?:boolean; definition:TpCalendar; }
interface Assignment {
  id:number; resourceId:number; resourceName:string; resourceType:string|null; initials:string|null;
  maxUnits:number; units:number; work:number; start:string|null; finish:string|null;
  booking:string; contour:string; rateTable:string;
  timephased:TpPeriod[]; timephasedManual:boolean;
  cost:number; currency:string; rate:number; committedElsewhere:number; totalUnits:number; over:boolean;
}

@Component({
  selector: 'rp-wp-assignments',
  templateUrl: './wp-assignments.component.html',
  styleUrls: ['./wp-assignments.component.sass'],
  standalone: false,
})
export class WpAssignmentsComponent implements OnInit {
  @Input() public workPackage:WorkPackageResource;

  private cdr = inject(ChangeDetectorRef);
  private wpId = '';

  readonly TYPE_COLOR:Record<string,string> = {
    work: '#0969da', equipment: '#8250df', material: '#bf3989', cost: '#1a7f37',
  };

  loading = true;
  error:string|null = null;
  canManage = false;
  contours:KV[] = [];
  rateTables:KV[] = [];
  bookings:KV[] = [];
  calendars:RpCal[] = [];
  pick:{ group:string; items:PickRes[] }[] = [];
  assignments:Assignment[] = [];

  dialog = false;
  editId:number|null = null;
  pickerQuery = '';
  form:any = null;

  ngOnInit():void {
    this.wpId = String(this.workPackage.id);
    this.reload();
  }

  reload():void {
    this.loading = true;
    fetch(`/work_packages/${this.wpId}/resource_planner/assignments`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        this.canManage = d.canManage;
        this.contours = d.config.contours; this.rateTables = d.config.rateTables; this.bookings = d.config.bookings;
        this.calendars = d.calendars || [];
        this.pick = d.resources; this.assignments = d.assignments;
        this.loading = false; this.cdr.detectChanges();
      }).catch(() => { this.error = 'Failed to load assignments.'; this.loading = false; this.cdr.detectChanges(); });
  }

  typeColor(t:string|null):string { return this.TYPE_COLOR[t || 'work'] || '#0969da'; }
  money(v:number, cur:string):string {
    const s:Record<string,string> = { EUR: '€', USD: '$', GBP: '£' };
    return (s[cur] || '') + Number(v || 0).toLocaleString('en-US');
  }
  bookingName(k:string):string { return (this.bookings.find((b) => b.key === k) || { name: k }).name; }

  // ── picker ──
  get allPickItems():PickRes[] { return this.pick.flatMap((g) => g.items); }
  get assignedIds():number[] { return this.assignments.map((a) => a.resourceId); }
  filteredGroups() {
    const q = this.pickerQuery.trim().toLowerCase();
    return this.pick
      .map((g) => ({ group: g.group, items: g.items.filter((r) => !q || r.name.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length);
  }
  pickedRes():PickRes|undefined { return this.allPickItems.find((r) => r.id === this.form?.resourceId); }
  hasUnits(t:string|null|undefined):boolean { return t === 'work' || t === 'equipment'; }

  // ── dialog ──
  openAdd():void {
    this.error = null; this.editId = null; this.pickerQuery = '';
    this.form = {
      resourceId: null, units: 100, work: 40,
      start: this.wpStart(), finish: this.wpFinish(),
      booking: 'committed', contour: 'flat', rateTable: 'A',
      timephased: [] as TpPeriod[], timephasedManual: false,
    };
    this.dialog = true;
  }
  openEdit(a:Assignment):void {
    this.error = null; this.editId = a.id;
    this.form = {
      resourceId: a.resourceId, units: a.units, work: a.work, start: a.start, finish: a.finish,
      booking: a.booking, contour: a.contour, rateTable: a.rateTable,
      timephased: (a.timephased || []).map((p) => ({ ...p })), timephasedManual: a.timephasedManual,
    };
    if (!this.form.timephasedManual) { this.recalcTimephased(); }
    this.dialog = true;
  }
  closeDialog():void { this.dialog = false; this.form = null; }

  pickResource(r:PickRes):void {
    if (this.assignedIds.includes(r.id) && this.editId === null) { return; }
    this.form.resourceId = r.id;
    this.form.units = this.hasUnits(r.resourceType) ? Math.min(100, r.maxUnits || 100) : 0;
    this.recalcTimephased();
  }

  // ── timephased work grid ──────────────────────────────────────────
  private calFor(res:PickRes|undefined):TpCalendar|null {
    if (!res) { return null; }
    const cal = this.calendars.find((c) => c.id === res.calendarId)
      || this.calendars.find((c) => c.isDefault) || this.calendars[0];
    return cal ? cal.definition : null;
  }
  // id → definition, for parentCalendarId inheritance in the resolver.
  private lookup:CalLookup = (id:number) => this.calendars.find((c) => c.id === id)?.definition;

  // Regenerate the grid from work × contour over the resource calendar, unless
  // the user has hand-edited a cell (manual override sticks).
  recalcTimephased():void {
    if (!this.form || this.form.timephasedManual) { return; }
    this.form.timephased = buildTimephased(
      this.calFor(this.pickedRes()), this.form.start, this.form.finish, +this.form.work || 0, this.form.contour, this.lookup,
    );
  }
  onScheduleChange():void { this.recalcTimephased(); }

  editCell(i:number, value:string):void {
    this.form.timephasedManual = true;
    this.form.timephased[i].hours = +value || 0;
    this.form.work = Math.round(this.form.timephased.reduce((s:number, p:TpPeriod) => s + (+p.hours || 0), 0) * 100) / 100;
  }
  resetContour():void { this.form.timephasedManual = false; this.recalcTimephased(); }
  tpTotal():number { return (this.form?.timephased || []).reduce((s:number, p:TpPeriod) => s + (+p.hours || 0), 0); }
  tpWeekday(dateISO:string):string { return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][parseISO(dateISO).getDay()]; }
  tpDayNum(dateISO:string):number { return parseISO(dateISO).getDate(); }

  presets():number[] {
    const r = this.pickedRes(); const max = r ? r.maxUnits : 0;
    if (!max) { return []; }
    if (max <= 100) { return [25, 50, 75, 100]; }
    const persons = Math.round(max / 100);
    if (persons <= 4) { const a:number[] = []; for (let p = 100; p <= max; p += 100) { a.push(p); } return a; }
    return [100, Math.round(persons / 2) * 100, max];
  }
  step(delta:number):void {
    const r = this.pickedRes(); const s = (r && r.maxUnits > 100) ? 50 : 25;
    this.form.units = Math.max(0, this.form.units + delta * s);
  }

  // Standard rate effective on a date for the selected table (rate tables over
  // time). Falls back to the flat rate when the table has no tiers — matches the
  // backend Assignment#effective_rate.
  private effectiveRate(r:PickRes, dateISO:string|null):number {
    const rows = (r.rateTables && r.rateTables[this.form.rateTable]) || [];
    if (rows.length) {
      const picked = this.pickEffective(rows.map((x) => ({ d: x.effectiveDate, v: x.rate })), dateISO);
      return picked != null ? picked : (r.rate || 0);
    }
    return r.rate || 0;
  }
  private effectiveMax(r:PickRes, dateISO:string|null):number {
    const ranges = r.availabilities || [];
    if (dateISO && ranges.length) {
      const hit = ranges.find((a) => (!a.from || dateISO >= a.from) && (!a.to || dateISO <= a.to));
      if (hit) { return hit.maxUnits; }
    }
    return r.maxUnits;
  }
  // Latest row whose date <= target (null date = earliest); earliest if all future.
  private pickEffective(rows:{ d:string|null; v:number }[], dateISO:string|null):number|null {
    if (!rows.length) { return null; }
    const sorted = [...rows].sort((a, b) => (a.d || '0000').localeCompare(b.d || '0000'));
    if (!dateISO) { return sorted[0].v; }
    const eligible = sorted.filter((x) => !x.d || x.d <= dateISO);
    return (eligible.length ? eligible[eligible.length - 1] : sorted[0]).v;
  }

  liveCost():{ cost:number; rate:number; cur:string; usesTable:boolean }|null {
    const r = this.pickedRes(); if (!r) { return null; }
    const periods:TpPeriod[] = this.form.timephased || [];
    const labour = periods.length
      ? periods.reduce((s, p) => s + (+p.hours || 0) * this.effectiveRate(r, p.date), 0)
      : (this.form.work || 0) * this.effectiveRate(r, this.form.start);
    const usesTable = !!(r.rateTables && (r.rateTables[this.form.rateTable] || []).length);
    return { cost: Math.round(labour), rate: this.effectiveRate(r, this.form.start), cur: r.currency, usesTable };
  }
  liveAlloc():{ total:number; max:number; elsewhere:number; over:boolean }|null {
    const r = this.pickedRes(); if (!r || !this.hasUnits(r.resourceType)) { return null; }
    const max = this.effectiveMax(r, this.form.start);
    const total = (r.committedElsewhere || 0) + (this.form.units || 0);
    return { total, max, elsewhere: r.committedElsewhere || 0, over: max > 0 && total > max };
  }

  private wpStart():string { return (this.workPackage as any).startDate || (this.workPackage as any).date || ''; }
  private wpFinish():string { return (this.workPackage as any).dueDate || (this.workPackage as any).date || ''; }

  save():void {
    if (!this.form.resourceId) { this.error = 'Please pick a resource.'; return; }
    const payload:Record<string, unknown> = {
      resource_planner_resource_id: this.form.resourceId, units: this.form.units, work_hours: this.form.work,
      start_date: this.form.start || null, finish_date: this.form.finish || null,
      booking: this.form.booking, contour: this.form.contour, rate_table: this.form.rateTable,
    };
    // Only send a manual grid; otherwise the backend regenerates from contour
    // (identical to the client preview) so both stay in lock-step.
    if (this.form.timephasedManual && (this.form.timephased || []).length) {
      payload['timephased'] = this.form.timephased.map((p:TpPeriod) => ({ date: p.date, hours: +p.hours || 0 }));
    }
    const url = this.editId
      ? `/work_packages/${this.wpId}/resource_planner/assignments/${this.editId}`
      : `/work_packages/${this.wpId}/resource_planner/assignments`;
    const method = this.editId ? 'PATCH' : 'POST';
    fetch(url, {
      method, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify({ assignment: payload }),
    }).then((r) => {
      if (r.ok) { this.closeDialog(); this.reload(); return undefined; }
      return r.json().then((j:any) => { this.error = (j.errors || ['Save failed']).join(', '); this.cdr.detectChanges(); });
    }).catch(() => { this.error = 'Network error.'; this.cdr.detectChanges(); });
  }

  remove(a:Assignment):void {
    if (!window.confirm(`Remove ${a.resourceName} from this work package?`)) { return; }
    fetch(`/work_packages/${this.wpId}/resource_planner/assignments/${a.id}`,
      { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrf() } })
      .then((r) => { if (r.ok) { this.reload(); } else { this.error = 'Delete failed'; this.cdr.detectChanges(); } });
  }

  private csrf():string {
    const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement|null;
    return el ? el.content : '';
  }
}
